/**
 * Pure tile-placement math shared by the table renderers. No canvas, no
 * palette — given counts + pixel extents it returns positions, so the logic
 * is unit-testable without a `CanvasRenderingContext2D`.
 *
 * `spreadRow` lays a rack/stack of same-size tiles along one axis, centring
 * them when they fit and overlapping down to a floor stride when they don't
 * (the mahjong analogue of domino's `layoutOpponentStrip`). `gridSlots` wraps
 * a discard pile into rows inside a pond cell. `splitPondCell` carves a
 * seat's pond cell into a meld strip + the remaining discard area.
 */

import type { Rect, SeatPosition } from '../layout/types.js';

export interface RowSpread {
  /** Centre-to-centre distance between adjacent tiles along the axis. */
  readonly stride: number;
  /** Axis offset (from the region start) of the FIRST tile's centre. */
  readonly startCenter: number;
  /** Tiles that actually fit — may be < requested when even the floor
   *  stride overflows the available length. */
  readonly visibleCount: number;
}

/**
 * Place `count` tiles of major-axis extent `tileMajor` along a region of
 * length `availableMajor`. Prefers a `tileMajor + gap` stride and centres the
 * block; compresses toward `minStride` when the block is too long; clips the
 * visible count only when the floor stride still overflows.
 */
export function spreadRow(
  count: number,
  tileMajor: number,
  gap: number,
  minStride: number,
  availableMajor: number,
): RowSpread {
  if (count <= 0) return { stride: 0, startCenter: 0, visibleCount: 0 };
  if (count === 1) return { stride: 0, startCenter: availableMajor / 2, visibleCount: 1 };

  const preferredStride = tileMajor + gap;
  const preferredBlock = (count - 1) * preferredStride + tileMajor;
  if (preferredBlock <= availableMajor) {
    return {
      stride: preferredStride,
      startCenter: (availableMajor - preferredBlock) / 2 + tileMajor / 2,
      visibleCount: count,
    };
  }

  const fitStride = (availableMajor - tileMajor) / (count - 1);
  if (fitStride >= minStride) {
    return { stride: fitStride, startCenter: tileMajor / 2, visibleCount: count };
  }

  const visibleCount = Math.max(
    1,
    Math.min(count, Math.floor((availableMajor - tileMajor) / minStride) + 1),
  );
  const block = (visibleCount - 1) * minStride + tileMajor;
  return {
    stride: minStride,
    startCenter: (availableMajor - block) / 2 + tileMajor / 2,
    visibleCount,
  };
}

export interface Slot {
  readonly cx: number;
  readonly cy: number;
}

export type GridAxis = 'horizontal' | 'vertical';

/** Which edge of the WRAP axis the first line sits against. */
export type GridWrapFrom = 'start' | 'end';

/**
 * Wrap `count` items of on-screen footprint `fpX × fpY` into a grid inside
 * `cell`. `axis` picks which direction fills first — `'horizontal'` (the
 * default) lays out left-to-right, wrapping to a new row only once a row fills
 * the cell's width, so a pile grows top-to-bottom; `'vertical'` lays out
 * top-to-bottom, wrapping to a new column only once a column fills the cell's
 * height, so a pile grows left-to-right instead — for the left/right seats'
 * sideways racks, whose discards should stack DOWN their side of the table
 * rather than run in a wide horizontal row. `wrapFrom` picks which edge the
 * first line hugs, so a pond can grow away from its own seat. Returns tile
 * CENTRES.
 *
 * The grid is CENTRED in `cell`, but centred **at its full capacity — never on
 * the live count**, and that distinction is the whole ballgame. Slot `i` stays
 * a pure function of `i`: a discard pile is an append-only log of physical
 * tiles on a table, so a tile once thrown must never move again.
 *
 * Centring on the live count is what made the pond appear to shuffle tiles
 * between players. Every change to the count nudged the whole pile — each
 * discard shifted the row it landed in by half a stride, and a Pung/Chi, which
 * removes a tile from the MIDDLE of the discarder's pile, slid everything after
 * it back a slot and re-centred the block on top of that. The claimed tile also
 * (correctly) relocates into the claimer's meld strip, so the two together read
 * as tiles crossing the table.
 *
 * Centring a full-capacity grid keeps the look — the pile sits in the middle of
 * its cell rather than jammed against one corner — while every slot stays put.
 * The visible cost is that a part-filled line is centred as though it were
 * full, so an early tile sits toward that line's start rather than dead centre.
 *
 * A line may still exceed the cell's cross-extent for a very long pile — a
 * separate, pre-existing limitation of the static pond.
 */
export function gridSlots(
  cell: Rect,
  count: number,
  fpX: number,
  fpY: number,
  gap: number,
  axis: GridAxis = 'horizontal',
  wrapFrom: GridWrapFrom = 'start',
): Slot[] {
  if (count <= 0) return [];
  const vertical = axis === 'vertical';
  // "Major" is the axis items fill first; "minor" is the axis that wraps.
  const cellMajor = vertical ? cell.height : cell.width;
  const cellMinor = vertical ? cell.width : cell.height;
  const fpMajor = vertical ? fpY : fpX;
  const fpMinor = vertical ? fpX : fpY;
  const majorOrigin = vertical ? cell.y : cell.x;
  const minorOrigin = vertical ? cell.x : cell.y;

  const perLine = Math.max(1, Math.floor((cellMajor + gap) / (fpMajor + gap)));
  const maxLines = Math.max(1, Math.floor((cellMinor + gap) / (fpMinor + gap)));
  // Extents of a FULL grid, so the centring below never sees `count`.
  const lineExtent = perLine * fpMajor + (perLine - 1) * gap;
  const blockExtent = maxLines * fpMinor + (maxLines - 1) * gap;

  const majorStart = majorOrigin + Math.max(0, (cellMajor - lineExtent) / 2) + fpMajor / 2;
  const minorBase = minorOrigin + Math.max(0, (cellMinor - blockExtent) / 2);
  const minorStart =
    wrapFrom === 'end' ? minorBase + blockExtent - fpMinor / 2 : minorBase + fpMinor / 2;
  const minorStep = (wrapFrom === 'end' ? -1 : 1) * (fpMinor + gap);

  const slots: Slot[] = [];
  for (let i = 0; i < count; i += 1) {
    const major = majorStart + (i % perLine) * (fpMajor + gap);
    const minor = minorStart + Math.floor(i / perLine) * minorStep;
    slots.push(vertical ? { cx: minor, cy: major } : { cx: major, cy: minor });
  }
  return slots;
}

/**
 * Which edge a seat's discard grid wraps from: the first row hugs that seat's
 * own meld strip (see `splitPondCell`) and later rows build toward the pond
 * centre, so a growing pile never crowds the seat it belongs to. Shared by the
 * renderer and the hit-test for the same reason `gridSlots` itself is — the two
 * must not be able to disagree about where a tile sits.
 */
export function pondWrapFrom(position: SeatPosition): GridWrapFrom {
  return position === 'bottom' || position === 'right' ? 'end' : 'start';
}

/**
 * Lay `count` meld tiles of major-axis extent `tileMajor` along `zone`,
 * anchored to the trailing end (inset by `endInset`) and centred on the
 * cross axis. Extracted from `HandRenderer.drawMelds` so hit-testing can
 * share the exact same positions instead of re-deriving them. Returns tile
 * CENTRES.
 */
export function meldSlots(
  zone: Rect,
  count: number,
  tileMajor: number,
  gap: number,
  endInset: number,
  horizontal: boolean,
): Slot[] {
  if (count <= 0) return [];
  const stride = tileMajor + gap;
  const block = (count - 1) * stride + tileMajor;
  const crossCenter = horizontal ? zone.y + zone.height / 2 : zone.x + zone.width / 2;
  const majorEnd = horizontal ? zone.x + zone.width : zone.y + zone.height;
  const firstCenter = majorEnd - endInset - block + tileMajor / 2;

  const slots: Slot[] = [];
  for (let i = 0; i < count; i += 1) {
    const major = firstCenter + i * stride;
    slots.push(horizontal ? { cx: major, cy: crossCenter } : { cx: crossCenter, cy: major });
  }
  return slots;
}

export interface PondCellSplit {
  /** Strip nearest that seat's rack — where a called set renders. */
  readonly meldCell: Rect;
  /** The rest of the cell, toward the pond centre — where discards render. */
  readonly discardCell: Rect;
}

/**
 * Largest share of a pond cell the meld strip may take. Pond cells are derived
 * from the viewport (`pond / POND_GRID`), not from tile size, so a small screen
 * can hand us a cell *narrower than one full-size meld tile* — a phone in
 * portrait gives the left/right seats roughly 68px of width against an 85px
 * strip. Without this cap the subtraction below runs past zero and the discard
 * area comes back as a rect with NEGATIVE extent. Melds get cramped on a small
 * screen either way; an inverted rectangle is the part that must not happen.
 */
const MAX_MELD_STRIP_FRACTION = 0.5;

/**
 * Carve a seat's pond cell into a MELD strip (up to `thickness` px, at the edge
 * nearest that seat's own rack) and the remaining DISCARD area. A called set
 * renders "under" that seat's discards — between the discard pile and their
 * rack — rather than beside their concealed hand.
 *
 * The strip is reserved whether or not the seat has actually called anything.
 * Carving it only once a meld exists would reclaim the space for the common
 * case, but it would also resize the discard area mid-hand — and every tile
 * already in that pile would jump, which is exactly what `gridSlots` was fixed
 * to stop.
 */
export function splitPondCell(
  cell: Rect,
  position: SeatPosition,
  thickness: number,
  gap = 0,
): PondCellSplit {
  const alongWidth = position === 'left' || position === 'right';
  const extent = alongWidth ? cell.width : cell.height;
  const strip = Math.max(0, Math.min(thickness, extent * MAX_MELD_STRIP_FRACTION));
  // Whatever is left after the strip is shared between the gap and the
  // discards, gap first — but the gap yields before the discard area is pushed
  // negative, since blank space is the one part of this nobody needs to see.
  const clear = Math.max(0, Math.min(gap, extent - strip));
  const taken = strip + clear;
  switch (position) {
    case 'bottom':
      return {
        meldCell: { x: cell.x, y: cell.y + cell.height - strip, width: cell.width, height: strip },
        discardCell: { x: cell.x, y: cell.y, width: cell.width, height: cell.height - taken },
      };
    case 'top':
      return {
        meldCell: { x: cell.x, y: cell.y, width: cell.width, height: strip },
        discardCell: { x: cell.x, y: cell.y + taken, width: cell.width, height: cell.height - taken },
      };
    case 'left':
      return {
        meldCell: { x: cell.x, y: cell.y, width: strip, height: cell.height },
        discardCell: { x: cell.x + taken, y: cell.y, width: cell.width - taken, height: cell.height },
      };
    case 'right':
      return {
        meldCell: { x: cell.x + cell.width - strip, y: cell.y, width: strip, height: cell.height },
        discardCell: { x: cell.x, y: cell.y, width: cell.width - taken, height: cell.height },
      };
  }
}
